import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels"; // import { includes } from "ramda";
import { htmlToText } from "@sinch/utils/dom";
import { isNilOrEmpty, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import { array, InferType, object, string } from "yup";
import type { Placeholders } from "./MessageForm";
import { useRecipientsChannelsAvailability } from "./useRecipientsChannelsAvailability";

export const minTemplateSchema = ({
  noRequiredSubject,
  placeholders = {},
  defaultOpened = NotificationChannel.Email,
}: {
  noRequiredSubject?: boolean;
  placeholders?: Partial<Record<NotificationChannel, Placeholders>>;
  defaultOpened?: NotificationChannel;
}) =>
  object().shape({
    channels: array().of(string<NotificationChannel>().required()).required().default([defaultOpened]).min(1),
    message: string()
      .when("channels", {
        is: (channels: string[]) => isNilOrEmpty(channels) || channels.includes(NotificationChannel.Email),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      })
      .default(""),
    pushMessage: string()
      .transform((value) => htmlToText(value))
      .when("channels", {
        is: (channels: string[]) => isNotNilOrEmpty(channels) && channels.includes(NotificationChannel.Push),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      })
      .default(""),
    textMessage: string()
      .transform((value) => htmlToText(value))
      .when("channels", {
        is: (channels: string[]) => isNotNilOrEmpty(channels) && channels.includes(NotificationChannel.Sms),
        then: (schema) => schema.required().smsMessage(placeholders[NotificationChannel.Sms] ?? []),
        otherwise: (schema) => schema,
      })
      .default(""),
    subject: string()
      .when("channels", {
        is: (channels: string[]) =>
          !noRequiredSubject && (isNilOrEmpty(channels) || !channels.includes(NotificationChannel.Sms)),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      })
      .default(""),
  });

export const messageSchema = ({
  initRecipients,
  noRequiredSubject,
  recipientsAvailability,
  placeholders = {},
  defaultOpened,
}: {
  initRecipients: string[];
  noRequiredSubject?: boolean;
  recipientsAvailability?: ReturnType<typeof useRecipientsChannelsAvailability>;
  placeholders?: Partial<Record<NotificationChannel, Placeholders>>;
  defaultOpened?: NotificationChannel;
}) =>
  object()
    .shape({
      recipients: array()
        .of(string().required())
        .required()
        .default(initRecipients ?? [])
        .test("not-empty-after-filter", (value, context) =>
          isNotNil(recipientsAvailability)
            ? recipientsAvailability.hasSendableRecipient(value || [], context.parent.channels)
            : true
        )
        .when("userGroups", {
          // A selected recipient group already targets people, so individual
          // recipients are only required when no group is chosen.
          is: (userGroups: string[]) => isNilOrEmpty(userGroups),
          then: (schema) => schema.min(1),
          otherwise: (schema) => schema,
        }),
      userGroups: array().of(string().required()).required().default([]),
    })
    .concat(minTemplateSchema({ noRequiredSubject, placeholders, defaultOpened }));

export type MessageFormFields = InferType<ReturnType<typeof messageSchema>>;
